// =============================================================================
// 2026-06-20 (user request "REKONSILIASI PROGRESS PEMBAYARAN LINTAS HALAMAN")
// + 2026-06-21 (user report produksi: retensi tidak terpotong dari Kas Harus
// Diterima, Retensi Belum JT selalu Rp0, dan 3 angka Sisa Piutang berbeda).
// Mirror frontend dari backend `utils/computation.compute_payment_breakdown`.
// SATU sumber angka untuk Summary Project, Monitoring (Ringkasan/Keuangan/
// Cash Flow), Laporan Keuangan, dan Export. Semua halaman WAJIB memakai fungsi
// ini (atau field `payment_breakdown` dari API monitoring).
//
// Formula penting (label tooltip):
//   Kas Harus Diterima       = nilai net per invoice (Bruto + PPN − PPh −
//                              Retensi inside) — snapshot billing engine bila
//                              tersedia, else formula legacy.
//   Sisa Piutang Jatuh Tempo = Σ per invoice max(0, Kas Harus Diterima −
//                              Uang Diterima) — overpay satu invoice TIDAK
//                              menutupi piutang invoice lain.
//   Retensi Belum Jatuh Tempo = retensi ditahan (termasuk derivasi dari persen
//                              retention_pct bila retention_amount kosong) yang
//                              belum dicairkan/ditagihkan & jatuh tempo > hari
//                              ini (atau belum diisi / diturunkan dari Selesai
//                              Fisik + Masa Retensi project).
//
// Nilai Kontrak KOSONG → `contractMissing=true` & `receiptBasisLabel=null`
// UI: tampilkan "Nilai Kontrak belum diisi".
// =============================================================================

const N = (v) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

// Invoice klaim/pencairan retensi — di luar universe invoice reguler
// (konsisten dengan Laporan Keuangan yang mengeksklusi is_retention_claim).
const isRetentionEntry = (e) =>
  Boolean(e?.is_retention_release || e?.is_retention_claim || (e?.retention_kind || "") === "release");

// Retensi efektif satu entry (mirror `entry_retention_info` backend) —
// fallback berantai: retention_amount → amount × retention_pct/100 (hanya
// tipe inside); persen/tipe: field entry → `_src_*` (konteks project/WO
// sumber yang disisipkan backend). Due date: retention_due_date →
// `_src_retention_due` (turunan Selesai Fisik + Masa Retensi).
export function entryRetentionInfo(e) {
  const retType = (e?.retention_type || e?._src_retention_type || "inside").toLowerCase();
  let ret = N(e?.retention_amount);
  if (ret <= 0 && retType === "inside") {
    const rp = N(e?.retention_pct ?? e?._src_retention_pct);
    const base = N(e?.amount ?? e?.gross_invoice_amount ?? e?.invoice_amount);
    if (rp > 0 && rp < 100 && base > 0) ret = Math.round(((base * rp) / 100) * 100) / 100;
  }
  return { ret, retType, due: e?.retention_due_date || e?._src_retention_due || null };
}

// Uang yang benar-benar diterima untuk satu income entry (mirror
// `_verified_received_amount` backend, spec 2026-06-19).
export function verifiedReceived(e) {
  if (!e) return 0;
  const receipts = e.payment_receipts || [];
  if (receipts.length) return Math.round(receipts.reduce((a, r) => a + N(r.amount), 0) * 100) / 100;
  if (!e.date) return 0;
  const net = N(e.net_received_amount || e.net_amount || e.net_received);
  if (net) return net;
  const amt = N(e.amount);
  const pph = N(e.pph_amount);
  const { ret, retType } = entryRetentionInfo(e);
  const r = retType === "inside" ? ret : 0;
  return pph || r ? Math.max(0, Math.round((amt - pph - r) * 100) / 100) : amt;
}

// Nilai NET yang diharapkan diterima per invoice (mirror
// `_expected_net_amount` backend) — snapshot billing engine lebih dulu.
export function expectedNet(e) {
  if (!e) return 0;
  const net = N(e.net_received_amount) || N(e.net_amount);
  if (net > 0) return net;
  const amt = N(e.gross_invoice_amount ?? e.invoice_amount ?? e.amount);
  if (amt <= 0) return 0;
  const pph = N(e.pph_amount);
  const { ret, retType } = entryRetentionInfo(e);
  const exp = amt - pph - (retType === "inside" ? ret : 0);
  return exp > 0 ? Math.round(exp * 100) / 100 : amt;
}

export function computePaymentBreakdown({
  contractValue,
  woValueSum = 0,
  voValueSum = 0,
  incomeEntries = [],
  today = null,
} = {}) {
  const _today = today ? new Date(today) : new Date();
  _today.setHours(0, 0, 0, 0);
  const cv = N(contractValue);
  const wo = N(woValueSum);
  const vo = N(voValueSum);

  let invoiceGross = 0, ppnTotal = 0, pphTotal = 0, retentionTotal = 0;
  let cashDue = 0, received = 0, receivedRegular = 0;
  let overdueSum = 0, overpaidTotal = 0, retentionNotYetDue = 0;
  let claimsTotal = 0, releasedTotal = 0;
  // 2026-10-18 (closeout #3): invoice draft/belum terverifikasi FAT → BUKAN
  // piutang resmi; dilacak terpisah (mirror backend compute_payment_breakdown).
  const _APPROVED_RS = ["approved", "verified", "VERIFIED_REFERENCE"];
  let draftReceivable = 0, draftCount = 0;
  // 2026-10-21 (user request): pemisahan eksplisit resmi vs draft — strip
  // informatif Monitoring (mirror backend compute_payment_breakdown).
  let officialCount = 0;
  let unverifiedCount = 0, unverifiedGross = 0, unverifiedCashDue = 0;
  let unverifiedReceived = 0, unverifiedOutstanding = 0, unverifiedNoDue = 0;

  for (const e of incomeEntries) {
    const rec = verifiedReceived(e);
    received += rec;
    if (isRetentionEntry(e)) {
      claimsTotal += N(e?.amount ?? e?.gross_invoice_amount ?? e?.invoice_amount);
      continue;
    }
    if (!_APPROVED_RS.includes(e?.review_status || "approved")) {
      const remDraft = expectedNet(e) - rec;
      if (remDraft > 0) { draftReceivable += remDraft; draftCount += 1; }
      unverifiedCount += 1;
      unverifiedGross += N(e.amount || e.gross_invoice_amount || e.invoice_amount);
      const cashD = expectedNet(e);
      unverifiedCashDue += cashD;
      unverifiedReceived += rec;
      unverifiedOutstanding += Math.max(cashD - rec, 0);
      if (!(e.due_date || e.invoice_due_date)) unverifiedNoDue += 1;
      continue;
    }
    receivedRegular += rec;
    officialCount += 1;
    const gross = N(e.amount || e.gross_invoice_amount || e.invoice_amount);
    const ppn = N(e.ppn_amount || e.vat_amount);
    const pph = N(e.pph_amount);
    const { ret, retType, due } = entryRetentionInfo(e);
    releasedTotal += N(e.retention_released_amount);
    invoiceGross += gross;
    ppnTotal += ppn;
    pphTotal += pph;
    retentionTotal += ret;
    const cash = expectedNet(e);
    cashDue += cash;
    const over = cash - rec;
    if (over > 0) overdueSum += over; else overpaidTotal += -over;

    if (ret > 0 && retType === "inside") {
      const outstandingRet = Math.max(0, ret - N(e.retention_released_amount));
      if (outstandingRet > 0) {
        if (due) {
          const d = new Date(String(due).slice(0, 10));
          if (isNaN(d.getTime()) || d > _today) retentionNotYetDue += outstandingRet;
        } else {
          retentionNotYetDue += outstandingRet;
        }
      }
    }
  }

  const r2 = (x) => Math.round(x * 100) / 100;
  received = r2(received);
  receivedRegular = r2(receivedRegular);
  invoiceGross = r2(invoiceGross);
  cashDue = r2(cashDue);
  overpaidTotal = r2(overpaidTotal);
  // Retensi yang sudah ditagihkan via invoice klaim/release BUKAN lagi "belum
  // jatuh tempo"; max(claims − released) mencegah double-subtract.
  retentionNotYetDue = r2(Math.max(0, retentionNotYetDue - Math.max(0, claimsTotal - releasedTotal)));
  const overdueReceivable = r2(overdueSum);

  // 2026-06-21 (user request): Nilai Kontrak = JUMLAHAN — umum: induk + VO;
  // maintenance: Σ WO. Basis penerimaan = total ini, label tunggal.
  const totalValue = r2(cv + wo + vo);
  let basisValue = 0, basisLabel = null;
  if (totalValue > 0) { basisValue = totalValue; basisLabel = "Nilai Kontrak"; }

  const receiptPct = basisLabel ? Math.round((received / basisValue) * 1000) / 10 : null;
  const invoiceSettlementPct = cashDue > 0 ? Math.round((received / cashDue) * 1000) / 10 : null;

  return {
    contractValue: r2(cv),
    contractMissing: cv <= 0,
    totalValue,
    woValueSum: r2(wo),
    voValueSum: r2(vo),
    invoiceGrossTotal: invoiceGross,
    ppnTotal: r2(ppnTotal),
    pphTotal: r2(pphTotal),
    retentionTotal: r2(retentionTotal),
    cashDueTotal: cashDue,
    receivedTotal: received,
    overdueReceivable,
    overpaidTotal,
    retentionNotYetDue,
    receiptBasisLabel: basisLabel,
    receiptBasisValue: r2(basisValue),
    receiptPct,
    receiptOver: basisLabel ? received > basisValue : false,
    invoiceSettlementPct,
    invoiceSettlementOver: cashDue > 0 ? received > cashDue : false,
    // Per-invoice clamp: cash_due + overpaid = received_reguler + overdue.
    reconciles: Math.abs((cashDue + overpaidTotal) - (receivedRegular + overdueReceivable)) < 0.01,
    draftReceivable: r2(draftReceivable),
    draftCount,
    officialInvoiceCount: officialCount,
    officialGrossAmount: invoiceGross,
    officialCashDue: cashDue,
    officialReceived: receivedRegular,
    officialOutstanding: overdueReceivable,
    unverifiedInvoiceCount: unverifiedCount,
    unverifiedGrossAmount: r2(unverifiedGross),
    unverifiedCashDue: r2(unverifiedCashDue),
    unverifiedReceived: r2(unverifiedReceived),
    unverifiedOutstanding: r2(unverifiedOutstanding),
    unverifiedNoDueCount: unverifiedNoDue,
  };
}

// Ambil breakdown dari API (`summary.payment_breakdown`) dengan alias
// camelCase supaya konsisten. Fallback ke computePaymentBreakdown bila kosong.
export function breakdownFromSummary(summary) {
  const b = summary?.payment_breakdown;
  if (!b) return null;
  return {
    contractValue: b.contract_value,
    contractMissing: b.contract_missing,
    totalValue: b.total_value,
    woValueSum: b.wo_value_sum,
    voValueSum: b.vo_value_sum,
    invoiceGrossTotal: b.invoice_gross_total,
    ppnTotal: b.ppn_total,
    pphTotal: b.pph_total,
    retentionTotal: b.retention_total,
    cashDueTotal: b.cash_due_total,
    receivedTotal: b.received_total,
    overdueReceivable: b.overdue_receivable,
    overpaidTotal: b.overpaid_total,
    retentionNotYetDue: b.retention_not_yet_due,
    receiptBasisLabel: b.receipt_basis_label,
    receiptBasisValue: b.receipt_basis_value,
    receiptPct: b.receipt_pct,
    receiptOver: b.receipt_over,
    invoiceSettlementPct: b.invoice_settlement_pct,
    invoiceSettlementOver: b.invoice_settlement_over,
    draftReceivable: b.draft_receivable,
    draftCount: b.draft_count,
    reconciles: b.reconciles,
    // 2026-10-21: pemisahan resmi vs draft (strip Monitoring Proyek).
    officialInvoiceCount: b.official_invoice_count,
    officialGrossAmount: b.official_gross_amount,
    officialCashDue: b.official_cash_due,
    officialReceived: b.official_received,
    officialOutstanding: b.official_outstanding,
    unverifiedInvoiceCount: b.unverified_invoice_count,
    unverifiedGrossAmount: b.unverified_gross_amount,
    unverifiedCashDue: b.unverified_cash_due,
    unverifiedReceived: b.unverified_received,
    unverifiedOutstanding: b.unverified_outstanding,
    unverifiedNoDueCount: b.unverified_no_due_count,
    // 2026-10-22: 6 bucket klasifikasi penerimaan kanonis.
    receivedOfficialKlasifikasi: b.received_official,
    advanceTotal: b.advance_total,
    retentionReceived: b.retention_received,
    unclassifiedTotal: b.unclassified_total,
    legacyUnlinkedTotal: b.legacy_unlinked_total,
    verifiedOverpaymentTotal: b.verified_overpayment_total,
    conflictTotal: b.conflict_total,
    nonArTotal: b.non_ar_total,
    totalReceipts: b.total_receipts,
    officialCollectibleAmount: b.official_collectible_amount,
    officialPaymentProgress: b.official_payment_progress,
    receiptOverOfficial: b.receipt_over_official,
    invoiceBasis: b.invoice_basis ?? b.official_collectible_amount,
    paymentProgressPercent: b.payment_progress_percent ?? b.official_payment_progress,
    paymentProgressStatus: b.payment_progress_status,
    paymentProgressFlags: b.payment_progress_flags || [],
  };
}

// Tooltip rumus — dipakai UI. Setiap angka WAJIB punya tooltip (spec user).
export const PAYMENT_FORMULA_TOOLTIPS = {
  contract_value: "Nilai Kontrak = total jumlahan — project umum: kontrak induk + Σ VO; maintenance: Σ nilai WO di dalamnya.",
  wo_value_sum: "Total Nilai WO = Σ contract_value semua WO anak (mengabaikan WO Dibatalkan).",
  invoice_gross: "Total Invoice Bruto = Σ amount (gross_invoice_amount) invoice non-retensi.",
  ppn: "PPN = Σ ppn_amount / vat_amount pada semua invoice.",
  pph: "Potongan PPh = Σ pph_amount (dipotong client, BUKAN piutang kas).",
  retention: "Retensi = Σ retention_amount; bila kosong diturunkan dari retention_pct (amount × persen ÷ 100, tipe inside) — termasuk persen yang diatur di level project/WO.",
  cash_due: "Kas Harus Diterima = nilai net per invoice (Bruto + PPN − PPh − Retensi inside) — memakai snapshot billing engine bila tersedia. Denominator progress pelunasan invoice.",
  received: "Uang Sudah Diterima = Σ payment_receipts terverifikasi (net setelah PPh & retensi).",
  overdue: "Sisa Piutang Jatuh Tempo = Σ per invoice max(0, Kas Harus Diterima − Uang Diterima). Kelebihan bayar satu invoice tidak menutupi piutang invoice lain; retensi belum jatuh tempo & invoice klaim retensi tidak termasuk.",
  retention_not_yet_due: "Retensi Belum Jatuh Tempo = retensi ditahan yang belum dicairkan/ditagihkan & retention_due_date > hari ini (atau belum diisi — diturunkan dari Selesai Fisik + Masa Retensi project).",
  receipt_pct: "Progress Penerimaan = Uang Diterima ÷ Nilai Kontrak (total jumlahan induk + VO/WO).",
  invoice_settlement_pct: "Pelunasan Invoice = Uang Diterima ÷ Kas Harus Diterima. PPh & retensi belum jatuh tempo TIDAK membuat invoice terlihat belum lunas.",
};
